// Styles tab — click any item, then click a look: predefined color/gradient/
// shadow/outline combos applied in one undo step (editor.applyStyle).
// Chip previews are CSS approximations; the canvas result is exact.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectionProps, isTextType } from '../hooks/useSelectionProps';
import { stylesForKind } from '../styles/stylePresets';

function chipStyleFor(preset) {
  const p = preset.props;
  const style = {};
  if (p.gradient) {
    const dir = p.gradient.direction === 'vertical' ? '180deg' : p.gradient.direction === 'diagonal' ? '135deg' : '90deg';
    style.background = `linear-gradient(${dir}, ${p.gradient.from}, ${p.gradient.to})`;
    style.WebkitBackgroundClip = 'text';
    style.WebkitTextFillColor = 'transparent';
  } else if (p.fill && p.fill !== 'transparent') {
    style.color = p.fill;
  } else if (p.fill === 'transparent') {
    style.color = 'transparent';
  }
  if (p.stroke) style.WebkitTextStroke = `1.5px ${p.stroke}`;
  if (p.shadow) style.textShadow = `${p.shadow.offsetX / 2}px ${p.shadow.offsetY / 2}px ${p.shadow.blur / 2}px ${p.shadow.color}`;
  if (p.textBackgroundColor) {
    style.backgroundColor = p.textBackgroundColor;
    style.padding = '0 6px';
    style.borderRadius = '3px';
  }
  return style;
}

function swatchStyleFor(preset) {
  const p = preset.props;
  const style = {};
  if (p.gradient) {
    const dir = p.gradient.direction === 'vertical' ? '180deg' : p.gradient.direction === 'diagonal' ? '135deg' : '90deg';
    style.background = `linear-gradient(${dir}, ${p.gradient.from}, ${p.gradient.to})`;
  } else if (p.fill && p.fill !== 'transparent') {
    style.background = p.fill;
  } else {
    style.background = 'transparent';
  }
  if (p.stroke) {
    style.border = `${Math.min(p.strokeWidth ?? 2, 4)}px ${p.strokeDashArray?.length ? 'dashed' : 'solid'} ${p.stroke}`;
  }
  if (p.shadow) style.boxShadow = `${p.shadow.offsetX / 2}px ${p.shadow.offsetY / 2}px ${p.shadow.blur / 2}px ${p.shadow.color}`;
  if (p.opacity !== undefined) style.opacity = Math.max(p.opacity, 0.4);
  return style;
}

function filterCssFor(p) {
  const f = p.filters || {};
  const parts = [];
  if (f.brightness) parts.push(`brightness(${1 + f.brightness})`);
  if (f.contrast) parts.push(`contrast(${1 + f.contrast})`);
  if (f.saturation) parts.push(`saturate(${1 + f.saturation})`);
  if (f.grayscale) parts.push('grayscale(1)');
  if (f.sepia) parts.push('sepia(0.8)');
  return parts.join(' ');
}

export default function StylesTab({ editor, selectedObjects }) {
  const { t } = useTranslation('editor');
  const { apply, refresh } = useSelectionProps(editor, selectedObjects);
  const obj = selectedObjects[0];
  if (!obj) return null;

  const kind = isTextType(obj.type)
    ? 'text'
    : obj.type === 'image'
      ? (obj.isIcon ? 'icon' : 'image')
      : obj.type === 'group'
        ? null
        : 'shape';

  if (!kind) {
    return (
      <div className="cce-inspector-content">
        <div className="cce-inspector-empty">{t('styles.ungroupHint')}</div>
      </div>
    );
  }

  const presets = stylesForKind(kind);
  const applyPreset = (preset) => {
    apply(() => editor?.applyStyle(preset), 'defer'); // applyStyle commits itself
    refresh();
  };

  return (
    <div className="cce-inspector-content">
      <div className="cce-styles-grid">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="cce-style-chip"
            onClick={() => applyPreset(preset)}
            title={t(`styles.presets.${preset.id}`, { defaultValue: preset.label })}
          >
            {kind === 'text' && (
              <span className="cce-style-chip-text" style={chipStyleFor(preset)}>Aa</span>
            )}
            {(kind === 'shape' || kind === 'icon') && (
              <span className="cce-style-chip-swatch" style={swatchStyleFor(preset)} />
            )}
            {kind === 'image' && (
              <span
                className="cce-style-chip-photo"
                style={{
                  filter: filterCssFor(preset.props),
                  boxShadow: preset.props.shadow
                    ? `0 3px 8px ${preset.props.shadow.color}`
                    : 'none',
                  border: preset.props.stroke
                    ? `3px solid ${preset.props.stroke}`
                    : 'none',
                }}
              />
            )}
            <span className="cce-style-chip-label">{t(`styles.presets.${preset.id}`, { defaultValue: preset.label })}</span>
          </button>
        ))}
      </div>
      <div className="cce-inspector-empty" style={{ paddingTop: 8 }}>
        {t('styles.previewNote')}
      </div>
    </div>
  );
}
